import type {
  Archetype,
  ArchetypeMatch,
  DimensionId,
  LikertResponse,
  LikertValue,
  QuizQuestion,
} from '../types/investmentPhilosophy';
import { DIMENSION_ORDER } from '../config/investmentArchetypes';

export function likertToSigned(r: LikertResponse): LikertValue {
  return (r - 3) as LikertValue;
}

export function buildUserProfile(
  responses: LikertResponse[],
  questions: QuizQuestion[]
): Record<DimensionId, number> {
  const sums: Record<DimensionId, number> = {
    microMacro: 0,
    valuationNarrative: 0,
    contrarianTrend: 0,
    concDiversified: 0,
    convictionCaution: 0,
  };
  const weightTotals: Record<DimensionId, number> = {
    microMacro: 0,
    valuationNarrative: 0,
    contrarianTrend: 0,
    concDiversified: 0,
    convictionCaution: 0,
  };

  questions.forEach((question, i) => {
    const response = responses[i];
    if (response == null) return;
    const signed = likertToSigned(response);
    question.impacts.forEach(({ dimension, polarity, weight = 1 }) => {
      sums[dimension] += signed * polarity * weight;
      weightTotals[dimension] += weight;
    });
  });

  const profile = {} as Record<DimensionId, number>;
  DIMENSION_ORDER.forEach((dim) => {
    profile[dim] = weightTotals[dim] > 0 ? sums[dim] / weightTotals[dim] : 0;
  });
  return profile;
}

export function euclideanDistance(
  user: Record<DimensionId, number>,
  archetype: Record<DimensionId, LikertValue>
): number {
  let sumSq = 0;
  for (const dim of DIMENSION_ORDER) {
    const diff = user[dim] - archetype[dim];
    sumSq += diff * diff;
  }
  return Math.sqrt(sumSq);
}

// Controls how sharply distance penalises similarity. Lower = sharper top match.
// 1.5 was tuned to give ~25% top-match for a mixed user and ~70% for a pure-archetype user.
const DISTANCE_FALLOFF = 1.5;

export function computeMatches(
  profile: Record<DimensionId, number>,
  archetypes: Archetype[]
): ArchetypeMatch[] {
  const similarities = archetypes.map((a) => {
    const distance = euclideanDistance(profile, a.vector);
    return { archetype: a, similarity: Math.exp(-distance / DISTANCE_FALLOFF) };
  });
  const totalSim = similarities.reduce((acc, s) => acc + s.similarity, 0);

  // Convert to floats first, round, then redistribute remainder so percentages sum to 100
  const floats = similarities.map((s) => ({
    archetypeId: s.archetype.id,
    rawSimilarity: s.similarity,
    floatPct: (s.similarity / totalSim) * 100,
  }));

  const rounded = floats.map((f) => ({ ...f, percentage: Math.floor(f.floatPct) }));
  let remainder = 100 - rounded.reduce((acc, r) => acc + r.percentage, 0);

  // Distribute the leftover percentage points to the archetypes with the largest fractional parts
  const byFraction = [...rounded]
    .map((r, idx) => ({ idx, frac: r.floatPct - r.percentage }))
    .sort((a, b) => b.frac - a.frac);

  for (let i = 0; i < remainder && i < byFraction.length; i++) {
    rounded[byFraction[i].idx].percentage += 1;
  }

  const matches: ArchetypeMatch[] = rounded.map((r) => ({
    archetypeId: r.archetypeId,
    percentage: r.percentage,
    rawSimilarity: r.rawSimilarity,
  }));

  matches.sort((a, b) => b.percentage - a.percentage || b.rawSimilarity - a.rawSimilarity);
  return matches;
}
